'use client';

import { useState, useRef, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { fetchMarketSearchData, fetchMarketComparison } from '@/lib/ai/geminiCache';
import type { CityPin } from './ExpansionMap';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface SearchedMarket {
  id: string;
  city: string;
  state: string;
  lat: number;
  lng: number;
  aiScore: number;
  population: string;
  medianHomeValue: string;
  stormHistory: string;
  competitionDensity: string;
  marketBrief: string;
  loading: boolean;
}

interface MarketSearchPanelProps {
  onSaveToExpansion: (market: SearchedMarket) => void;
  onSearchPinsChange: (pins: CityPin[]) => void;
  existingPins: CityPin[];
}

// ─── Geocode helper (uses existing API route) ─────────────────────────────────
async function geocodeCity(query: string): Promise<{ lat: number; lng: number; city: string; state: string } | null> {
  try {
    const res = await fetch(`/api/geocode-address?address=${encodeURIComponent(query)}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (data.lat && data.lng) {
      // Parse city/state from query
      const parts = query.split(',').map((s) => s.trim());
      return {
        lat: data.lat,
        lng: data.lng,
        city: parts[0] ?? query,
        state: parts[1]?.toUpperCase().slice(0, 2) ?? '',
      };
    }
    return null;
  } catch {
    return null;
  }
}

// ─── Score color helper ───────────────────────────────────────────────────────
function scoreColor(score: number) {
  if (score >= 75) return '#10B981';
  if (score >= 50) return '#F59E0B';
  return '#EF4444';
}

function scoreLabel(score: number) {
  if (score >= 75) return 'High Opportunity';
  if (score >= 50) return 'Moderate';
  return 'Low';
}

// ─── Single Market Result Card ────────────────────────────────────────────────
interface MarketResultCardProps {
  market: SearchedMarket;
  slot: 'A' | 'B';
  onSave: (market: SearchedMarket) => void;
  onClear: () => void;
  alreadySaved: boolean;
}

function MarketResultCard({ market, slot, onSave, onClear, alreadySaved }: MarketResultCardProps) {
  const sc = scoreColor(market.aiScore);

  return (
    <div
      className="rounded-2xl overflow-hidden flex flex-col"
      style={{ background: '#fff', border: '1px solid #E5E7EB', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
    >
      {/* Card header */}
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ background: 'linear-gradient(135deg, #1C2E5E 0%, #1C2E5E 100%)' }}
      >
        <div className="flex items-center gap-2">
          <div
            className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-black text-white"
            style={{ background: 'rgba(232,154,60,0.3)' }}
          >
            {slot}
          </div>
          <div>
            <div className="text-sm font-bold text-white">
              {market.city}{market.state ? `, ${market.state}` : ''}
            </div>
            <div className="text-[10px]" style={{ color: 'rgba(255,255,255,0.55)' }}>Search Result</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* AI Score badge */}
          {market.loading ? (
            <div className="w-4 h-4 rounded-full border-2 border-[#E89A3C] border-t-transparent animate-spin" />
          ) : (
            <div
              className="flex items-center gap-1 px-2.5 py-1 rounded-full"
              style={{ background: `${sc}22`, border: `1.5px solid ${sc}` }}
            >
              <Icon name="SparklesIcon" size={10} style={{ color: sc }} />
              <span className="text-xs font-black" style={{ color: sc }}>{market.aiScore}</span>
              <span className="text-[9px] font-semibold" style={{ color: sc }}>/100</span>
            </div>
          )}
          <button
            onClick={onClear}
            className="w-6 h-6 rounded-lg flex items-center justify-center hover:bg-white/10"
            style={{ color: 'rgba(255,255,255,0.6)' }}
          >
            <Icon name="XMarkIcon" size={13} />
          </button>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-2 p-4">
        {[
          { label: 'Population', value: market.population, icon: 'UsersIcon' },
          { label: 'Median Home Value', value: market.medianHomeValue, icon: 'HomeIcon' },
          { label: 'Storm History', value: market.stormHistory, icon: 'CloudIcon' },
          { label: 'Competition', value: market.competitionDensity, icon: 'BuildingOfficeIcon' },
        ].map((stat) => (
          <div
            key={stat.label}
            className="rounded-xl p-3"
            style={{ background: '#F9FAFB', border: '1px solid #F3F4F6' }}
          >
            <div className="text-[10px] font-semibold uppercase tracking-wide mb-1" style={{ color: '#9CA3AF' }}>
              {stat.label}
            </div>
            {market.loading ? (
              <div className="h-4 rounded animate-pulse" style={{ background: '#E5E7EB', width: '70%' }} />
            ) : (
              <div className="text-xs font-bold" style={{ color: '#1C2E5E' }}>{stat.value}</div>
            )}
          </div>
        ))}
      </div>

      {/* AI Score bar */}
      {!market.loading && (
        <div className="px-4 pb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#6B7280' }}>AI Market Score</span>
            <span className="text-[10px] font-bold" style={{ color: sc }}>{scoreLabel(market.aiScore)}</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: '#F3F4F6' }}>
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${market.aiScore}%`, background: `linear-gradient(90deg, ${sc}88, ${sc})` }}
            />
          </div>
        </div>
      )}

      {/* Gemini Market Brief */}
      <div
        className="mx-4 mb-4 rounded-xl p-3"
        style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)', border: '1px solid #DBEAFE' }}
      >
        <div className="flex items-center gap-1.5 mb-1.5">
          <div
            className="w-4 h-4 rounded-md flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}
          >
            <Icon name="SparklesIcon" size={8} className="text-white" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#1C2E5E' }}>
            Gemini Market Brief
          </span>
        </div>
        {market.loading ? (
          <div className="space-y-1.5">
            <div className="h-2.5 rounded-full animate-pulse" style={{ background: 'rgba(3,52,148,0.1)', width: '95%' }} />
            <div className="h-2.5 rounded-full animate-pulse" style={{ background: 'rgba(3,52,148,0.07)', width: '80%' }} />
            <div className="h-2.5 rounded-full animate-pulse" style={{ background: 'rgba(3,52,148,0.05)', width: '88%' }} />
          </div>
        ) : (
          <p className="text-[11px] leading-relaxed" style={{ color: '#1E3A5F' }}>{market.marketBrief}</p>
        )}
      </div>

      {/* Save button */}
      <div className="px-4 pb-4 mt-auto">
        <button
          onClick={() => onSave(market)}
          disabled={alreadySaved || market.loading}
          className="w-full py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50"
          style={{
            background: alreadySaved
              ? '#ECFDF5' :'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)',
            color: alreadySaved ? '#10B981' : '#fff',
            border: alreadySaved ? '1.5px solid #A7F3D0' : 'none',
          }}
        >
          {alreadySaved ? (
            <>
              <Icon name="CheckIcon" size={13} />
              Saved to Expansion List
            </>
          ) : (
            <>
              <Icon name="PlusIcon" size={13} />
              Save to Expansion List
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Comparison Banner ────────────────────────────────────────────────────────
interface ComparisonBannerProps {
  marketA: SearchedMarket;
  marketB: SearchedMarket;
}

function ComparisonBanner({ marketA, marketB }: ComparisonBannerProps) {
  const [comparison, setComparison] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);

  const runComparison = useCallback(async () => {
    if (hasFetched.current || marketA.loading || marketB.loading) return;
    hasFetched.current = true;
    setLoading(true);

    try {
      const text = await fetchMarketComparison(
        { city: marketA.city, state: marketA.state, aiScore: marketA.aiScore, population: marketA.population, stormHistory: marketA.stormHistory, competitionDensity: marketA.competitionDensity },
        { city: marketB.city, state: marketB.state, aiScore: marketB.aiScore, population: marketB.population, stormHistory: marketB.stormHistory, competitionDensity: marketB.competitionDensity }
      );
      setComparison(text);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [marketA, marketB]);

  // Auto-run when both markets are loaded
  if (!marketA.loading && !marketB.loading && !hasFetched.current) {
    runComparison();
  }

  const winner = marketA.aiScore >= marketB.aiScore ? marketA : marketB;

  return (
    <div
      className="rounded-2xl p-4 flex items-start gap-3"
      style={{ background: 'linear-gradient(135deg, #1C2E5E 0%, #1C2E5E 100%)', border: '1px solid rgba(232,154,60,0.3)' }}
    >
      <div
        className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: 'rgba(232,154,60,0.25)' }}
      >
        <Icon name="ScaleIcon" size={16} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Gemini Side-by-Side Comparison
          </span>
          {(loading || marketA.loading || marketB.loading) && (
            <div className="w-3 h-3 rounded-full border border-[#E89A3C] border-t-transparent animate-spin" />
          )}
        </div>
        {loading || marketA.loading || marketB.loading ? (
          <div className="space-y-1.5">
            <div className="h-3 rounded-full animate-pulse" style={{ background: 'rgba(255,255,255,0.15)', width: '90%' }} />
            <div className="h-3 rounded-full animate-pulse" style={{ background: 'rgba(255,255,255,0.1)', width: '70%' }} />
          </div>
        ) : comparison ? (
          <p className="text-sm font-semibold text-white leading-snug">{comparison}</p>
        ) : (
          <p className="text-sm text-white" style={{ opacity: 0.6 }}>
            AI recommends: <span className="font-bold" style={{ color: '#E89A3C' }}>{winner.city}, {winner.state}</span> (Score: {winner.aiScore}/100)
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function MarketSearchPanel({
  onSaveToExpansion,
  onSearchPinsChange,
  existingPins,
}: MarketSearchPanelProps) {
  const [searchA, setSearchA] = useState('');
  const [searchB, setSearchB] = useState('');
  const [marketA, setMarketA] = useState<SearchedMarket | null>(null);
  const [marketB, setMarketB] = useState<SearchedMarket | null>(null);
  const [searchingA, setSearchingA] = useState(false);
  const [searchingB, setSearchingB] = useState(false);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());

  // Update temporary map pins whenever results change
  const updateSearchPins = useCallback(
    (a: SearchedMarket | null, b: SearchedMarket | null) => {
      const pins: CityPin[] = [];
      if (a) {
        pins.push({
          id: `search-${a.id}`,
          city_name: a.city,
          state: a.state,
          latitude: a.lat,
          longitude: a.lng,
          city_status: 'prospecting',
          radius_rings: [],
        });
      }
      if (b) {
        pins.push({
          id: `search-${b.id}`,
          city_name: b.city,
          state: b.state,
          latitude: b.lat,
          longitude: b.lng,
          city_status: 'prospecting',
          radius_rings: [],
        });
      }
      onSearchPinsChange(pins);
    },
    [onSearchPinsChange]
  );

  const fetchMarketData = useCallback(
    async (query: string, slot: 'A' | 'B') => {
      if (!query.trim()) return;
      const setSearching = slot === 'A' ? setSearchingA : setSearchingB;
      const setMarket = slot === 'A' ? setMarketA : setMarketB;

      setSearching(true);

      // Geocode first
      const geo = await geocodeCity(query);
      const city = geo?.city ?? query.split(',')[0].trim();
      const state = geo?.state ?? query.split(',')[1]?.trim().toUpperCase().slice(0, 2) ?? '';
      const lat = geo?.lat ?? 39.5;
      const lng = geo?.lng ?? -98.35;

      const id = `${city}-${state}-${Date.now()}`;

      // Set loading placeholder
      const placeholder: SearchedMarket = {
        id,
        city,
        state,
        lat,
        lng,
        aiScore: 0,
        population: '—',
        medianHomeValue: '—',
        stormHistory: '—',
        competitionDensity: '—',
        marketBrief: '',
        loading: true,
      };
      setMarket(placeholder);
      updateSearchPins(slot === 'A' ? placeholder : marketA, slot === 'B' ? placeholder : marketB);
      setSearching(false);

      // Fetch AI data — uses geminiCache for deduplication + 2-hour TTL
      try {
        const data = await fetchMarketSearchData(city, state);

        const result: SearchedMarket = {
          id,
          city,
          state,
          lat,
          lng,
          aiScore: data.aiScore,
          population: data.population,
          medianHomeValue: data.medianHomeValue,
          stormHistory: data.stormHistory,
          competitionDensity: data.competitionDensity,
          marketBrief: data.marketBrief,
          loading: false,
        };

        setMarket(result);
        updateSearchPins(
          slot === 'A' ? result : marketA,
          slot === 'B' ? result : marketB
        );
      } catch {
        setMarket((prev) => prev ? { ...prev, loading: false, marketBrief: 'Unable to generate brief. Please try again.' } : null);
      }
    },
    [marketA, marketB, updateSearchPins]
  );

  const handleSearch = (slot: 'A' | 'B') => {
    const query = slot === 'A' ? searchA : searchB;
    fetchMarketData(query, slot);
  };

  const handleSave = (market: SearchedMarket) => {
    onSaveToExpansion(market);
    setSavedIds((prev) => new Set([...prev, market.id]));
  };

  const handleClear = (slot: 'A' | 'B') => {
    if (slot === 'A') {
      setMarketA(null);
      setSearchA('');
      updateSearchPins(null, marketB);
    } else {
      setMarketB(null);
      setSearchB('');
      updateSearchPins(marketA, null);
    }
  };

  const bothLoaded = marketA && marketB && !marketA.loading && !marketB.loading;

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: '#fff', boxShadow: '0 2px 16px rgba(0,0,0,0.07)', border: '1px solid #E5E7EB' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#F3F4F6' }}>
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}
          >
            <Icon name="MagnifyingGlassIcon" size={18} className="text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold" style={{ color: '#1C2E5E' }}>Market Search</h2>
            <p className="text-xs" style={{ color: '#9CA3AF' }}>
              Search any city · Gemini AI scores &amp; briefs · Compare side-by-side
            </p>
          </div>
        </div>
        <div
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
          style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)', color: '#1C2E5E', border: '1px solid #DBEAFE' }}
        >
          <Icon name="SparklesIcon" size={11} />
          Gemini Powered
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* Search inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Slot A */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#374151' }}>
              Market A
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Icon
                  name="MapPinIcon"
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: '#9CA3AF' }}
                />
                <input
                  type="text"
                  value={searchA}
                  onChange={(e) => setSearchA(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch('A')}
                  placeholder="City, State or Zip"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm border outline-none focus:ring-2 focus:ring-[#E89A3C]/30"
                  style={{ borderColor: '#E5E7EB', color: '#1C2E5E' }}
                />
              </div>
              <button
                onClick={() => handleSearch('A')}
                disabled={!searchA.trim() || searchingA}
                className="px-4 py-2.5 rounded-xl text-xs font-bold text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
                style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}
              >
                {searchingA ? (
                  <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                ) : (
                  <Icon name="MagnifyingGlassIcon" size={13} />
                )}
                Search
              </button>
            </div>
          </div>

          {/* Slot B */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#374151' }}>
              Market B <span className="text-[10px] font-normal normal-case" style={{ color: '#9CA3AF' }}>(optional — for comparison)</span>
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Icon
                  name="MapPinIcon"
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: '#9CA3AF' }}
                />
                <input
                  type="text"
                  value={searchB}
                  onChange={(e) => setSearchB(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch('B')}
                  placeholder="City, State or Zip"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm border outline-none focus:ring-2 focus:ring-[#E89A3C]/30"
                  style={{ borderColor: '#E5E7EB', color: '#1C2E5E' }}
                />
              </div>
              <button
                onClick={() => handleSearch('B')}
                disabled={!searchB.trim() || searchingB}
                className="px-4 py-2.5 rounded-xl text-xs font-bold text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
                style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}
              >
                {searchingB ? (
                  <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                ) : (
                  <Icon name="MagnifyingGlassIcon" size={13} />
                )}
                Search
              </button>
            </div>
          </div>
        </div>

        {/* Empty state */}
        {!marketA && !marketB && (
          <div
            className="rounded-2xl p-8 flex flex-col items-center justify-center text-center"
            style={{ background: '#F9FAFB', border: '2px dashed #E5E7EB' }}
          >
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
              style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)' }}
            >
              <Icon name="MagnifyingGlassCircleIcon" size={24} style={{ color: '#E89A3C' }} />
            </div>
            <p className="text-sm font-semibold" style={{ color: '#374151' }}>Search any market to get started</p>
            <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
              Gemini will score the market 1–100 and generate a brief with population, home values, storm history &amp; competition data
            </p>
          </div>
        )}

        {/* Results */}
        {(marketA || marketB) && (
          <div className={`grid gap-4 ${marketA && marketB ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 max-w-md'}`}>
            {marketA && (
              <MarketResultCard
                market={marketA}
                slot="A"
                onSave={handleSave}
                onClear={() => handleClear('A')}
                alreadySaved={savedIds.has(marketA.id)}
              />
            )}
            {marketB && (
              <MarketResultCard
                market={marketB}
                slot="B"
                onSave={handleSave}
                onClear={() => handleClear('B')}
                alreadySaved={savedIds.has(marketB.id)}
              />
            )}
          </div>
        )}

        {/* Comparison banner */}
        {bothLoaded && (
          <ComparisonBanner marketA={marketA!} marketB={marketB!} />
        )}

        {/* Temporary pin notice */}
        {(marketA || marketB) && (
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs"
            style={{ background: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E' }}
          >
            <Icon name="MapPinIcon" size={12} />
            <span>
              <strong>Temporary pins</strong> are shown on the map above as 🔍 Prospecting markers. Save to Expansion List to make them permanent.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
